// src/pages/Projects.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./css/projects.css";
import "../App.css";
import { request } from "../services/server";
import { useAuth } from "../services/AuthContext";
import Main from "./Main";
/**
 * Projects
 * - Fetches /users/workspaces/
 * - Uses workspace_key = orgnr for ALL workspace actions:
 *   PATCH  /users/workspaces/<orgnr>/
 *   DELETE /users/workspaces/<orgnr>/        (broker-admin only)
 *   DELETE /users/workspaces/<orgnr>/leave/  (self leave, idempotent)
 */

const STATUS_LABEL = {
  active: "Aktiv",
  aktiv: "Aktiv",
  pending: "Väntande",
  väntande: "Väntande",
  archived: "Arkiv",
  arkiv: "Arkiv",
  closing: "Stängs",
  closed: "Stängd",
  inactive: "Inaktiv",
  inaktiv: "Inaktiv",
  blocked: "Spärrad",
  unknown: "Okänd",
  all: "Alla statusar",
};

function statusLabel(s) {
  if (!s) return "Okänd";
  return STATUS_LABEL[String(s).toLowerCase()] || s;
}

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();

  // Lifecycle states added 2026-06: archived/closing/closed.
  // Closed + inactive both render as muted danger; closing as warning
  // since it's a transitional state the broker can still influence;
  // archived as info because the workspace is still operational.
  const cls =
    s === "active" || s.includes("aktiv")
      ? "root-badge root-badge--success"
      : s === "archived" || s.includes("arkiv")
      ? "root-badge root-badge--info"
      : s === "closing" || s.includes("stängs")
      ? "root-badge root-badge--warning"
      : s === "pending" || s.includes("vänt")
      ? "root-badge root-badge--warning"
      : s === "closed" || s.includes("stängd")
      ? "root-badge root-badge--danger"
      : s.includes("inactive") || s.includes("inaktiv") || s.includes("blocked") || s.includes("block")
      ? "root-badge root-badge--danger"
      : "root-badge root-badge--info";

  return <span className={cls}>{statusLabel(status)}</span>;
}

function flattenWorkspace(ws) {
  const org = ws?.organisation || {};
  const orgnr = String(org?.organisationnummer || "").trim();
  // Prefer the new workspace slug as the URL key — it's globally unique
  // (no collision when multiple broker tenants analyse the same orgnr)
  // and country-agnostic (works once we expand outside Sweden). Fall back
  // to orgnr for legacy workspaces that haven't been backfilled yet.
  const slug = (ws?.slug || "").trim();
  const urlKey = slug || orgnr;

  return {
    id: ws?.id ?? slug ?? orgnr ?? null,
    workspace_key: urlKey, // <- used in URLs and API paths
    slug,
    status: ws?.deal_status || "unknown",
    orgnr,
    name: org?.namn || "Namnlöst bolag",
    created_at: ws?.created_at || null,
    my_side: ws?.my_side || "",  // broker / seller / buyer / ""
    _raw: ws,
  };
}

function workspaceLandingUrl(project) {
  // workspace_key falls back to orgnr when slug is missing — see flattenWorkspace.
  const key = project.workspace_key || project.orgnr;
  switch (project.my_side) {
    case "seller": return `/seller/${key}`;
    case "buyer":  return `/buyer/${key}`;
    default:       return `/dashboard/${key}`;
  }
}

async function apiFetchWorkspaces({ statuses } = {}) {
  const qs = new URLSearchParams();
  if (Array.isArray(statuses) && statuses.length) {
    qs.set("status", statuses.join(","));
  }
  const path = `/users/workspaces/${qs.toString() ? `?${qs.toString()}` : ""}`;
  const data = await request(path, { method: "GET", credentials: "include" });

  const arr = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
  return arr.map(flattenWorkspace);
}

function sortIcon(active, dir) {
  if (!active) return "↕";
  return dir === "asc" ? "↑" : "↓";
}

export default function Projects() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isBroker = Boolean(user?.global_nav?.is_broker);

  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState(null);

  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [confirmOpenFor, setConfirmOpenFor] = useState(null);
  const menuRef = useRef(null);

  const [busyKey, setBusyKey] = useState(null); // workspace_key (orgnr)

  // Close menu on outside click / escape
  useEffect(() => {
    const onDown = (e) => {
      if (!menuRef.current) return;
      if (menuRef.current.contains(e.target)) return;
      setMenuOpenFor(null);
      setConfirmOpenFor(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpenFor(null);
        setConfirmOpenFor(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Loader for initial load + statusFilter changes
  const load = useCallback(async ({ statusFilterValue } = {}) => {
    setError(null);
    setLoading(true);

    try {
      const statuses = statusFilterValue && statusFilterValue !== "all" ? [statusFilterValue] : null;
      const ws = await apiFetchWorkspaces({ statuses });
      setProjects(ws);
    } catch (e) {
      const msg = e?.body?.detail || e?.body?.message || e?.message || "Failed to load workspaces.";
      setError(msg);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // initial load
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await load({ statusFilterValue: "all" });
    })();
    return () => {
      alive = false;
    };
  }, [load]);

  // refetch when statusFilter changes (no double-fetch on mount)
  useEffect(() => {
    if (loading) return;
    load({ statusFilterValue: statusFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Status dropdown values
  const statuses = useMemo(() => {
    const base = ["all", "active", "pending", "inactive", "unknown"];
    const set = new Set(base);
    for (const p of projects) if (p.status) set.add(p.status);
    return Array.from(set);
  }, [projects]);

  function toggleSort(nextKey) {
    setSortKey((prevKey) => {
      if (prevKey !== nextKey) {
        setSortDir("asc");
        return nextKey;
      }
      setSortDir((prevDir) => (prevDir === "asc" ? "desc" : "asc"));
      return prevKey;
    });
  }

  const filteredAndSorted = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = projects.filter((p) => {
      if (!needle) return true;
      const hay = [p.name, p.orgnr, p.status].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });

    const dir = sortDir === "asc" ? 1 : -1;

    return [...filtered].sort((a, b) => {
      if (sortKey === "name") return (a.name || "").localeCompare(b.name || "") * dir;
      if (sortKey === "status") return (a.status || "").localeCompare(b.status || "") * dir;

      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return (da - db) * dir;
    });
  }, [projects, q, sortKey, sortDir]);

  const updateLocalStatus = (workspaceKey, nextStatus) => {
    setProjects((prev) => prev.map((p) => (p.workspace_key === workspaceKey ? { ...p, status: nextStatus } : p)));
  };

  const removeLocalProject = (workspaceKey) => {
    setProjects((prev) => prev.filter((p) => p.workspace_key !== workspaceKey));
  };

  const handleChangeStatus = async (p, nextStatus) => {
    const key = p?.workspace_key;
    if (!key) return;

    setError(null);
    setBusyKey(key);
    setMenuOpenFor(null);
    setConfirmOpenFor(null);

    const prevStatus = p.status;
    updateLocalStatus(key, nextStatus);

    try {
      await request(`/users/workspaces/${key}/`, {
        method: "PATCH",
        credentials: "include",
        body: { deal_status: nextStatus },
      });
    } catch (e) {
      updateLocalStatus(key, prevStatus);

      const code = e?.status || e?.body?.status_code;
      if (code === 403) {
        setError("Du saknar behörighet att ändra status (kräver broker-admin på workspacet).");
      } else {
        const msg = e?.body?.detail || e?.message || "Could not update status.";
        setError(msg);
      }
    } finally {
      setBusyKey(null);
    }
  };

  const handleRemoveOrLeave = async (p) => {
    const key = p?.workspace_key;
    if (!key) return;

    setError(null);
    setBusyKey(key);

    try {
      // Try close workspace (broker admin only)
      await request(`/users/workspaces/${key}/`, { method: "DELETE", credentials: "include" });

      updateLocalStatus(key, "inactive");
      if (statusFilter !== "all" && statusFilter !== "inactive") {
        removeLocalProject(key);
      }
    } catch (e) {
      const code = e?.status || e?.body?.status_code;
      const isForbidden =
        code === 403 || (e?.body?.detail && String(e.body.detail).toLowerCase().includes("not allowed"));

      if (!isForbidden) {
        const msg = e?.body?.detail || e?.message || "Could not remove project.";
        setError(msg);
        return;
      }

      // fallback self-leave (NO user id needed)
      try {
        await request(`/users/workspaces/${key}/leave/`, { method: "DELETE", credentials: "include" });
      } catch (e2) {
        const code2 = e2?.status || e2?.body?.status_code;
        // If backend is not yet updated and returns 404, treat as already-left
        if (code2 !== 404) {
          const msg = e2?.body?.detail || e2?.message || "Could not leave workspace.";
          setError(msg);
          return;
        }
      }

      removeLocalProject(key);
    } finally {
      setBusyKey(null);
      setMenuOpenFor(null);
      setConfirmOpenFor(null);
    }
  };

  return (
    <Main className="projects-page root-container">
      <div className="projects-topbar">
        <div className="projects-titleBlock">
          <h1 className="root-h1">Projekt</h1>
          <div className="root-text-muted">{loading ? "Laddar…" : `${filteredAndSorted.length} totalt`}</div>
        </div>

        <div className="projects-actions">
          <input
            className="root-input projects-search"
            placeholder="Sök på namn, orgnr, SNI…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          <select
            className="root-select projects-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {statuses.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s)}
              </option>
            ))}
          </select>

          {isBroker && (
            <button className="root-btn root-btn--primary projects-createBtn" onClick={() => navigate("/check")}>
              Skapa nytt projekt
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="root-card">
          <div className="root-h3">Fel</div>
          <div className="root-text-muted" style={{ marginTop: 8 }}>
            {error}
          </div>
        </div>
      )}

      <div className="root-card">
        {loading ? (
          <div className="projects-empty">
            <div className="root-h3">Laddar…</div>
            <div className="root-text-muted">Hämtar dina projekt.</div>
          </div>
        ) : filteredAndSorted.length === 0 ? (
          <div className="projects-empty">
            <div className="root-h3">Inga projekt hittades</div>
            <div className="root-text-muted">Prova att rensa filtren eller söka efter något annat.</div>
          </div>
        ) : (
          <table className="root-table">
            <thead>
              <tr>
                <th className="projects-th" onClick={() => toggleSort("name")} role="button" tabIndex={0}>
                  Namn <span className="projects-sort">{sortIcon(sortKey === "name", sortDir)}</span>
                </th>
                <th className="projects-th" onClick={() => toggleSort("created_at")} role="button" tabIndex={0}>
                  Skapad <span className="projects-sort">{sortIcon(sortKey === "created_at", sortDir)}</span>
                </th>
                <th className="projects-th" onClick={() => toggleSort("status")} role="button" tabIndex={0} style={{ width: 200 }}>
                  Status <span className="projects-sort">{sortIcon(sortKey === "status", sortDir)}</span>
                </th>
                <th style={{ width: 56 }} />
              </tr>
            </thead>

            <tbody>
              {filteredAndSorted.map((p) => {
                const orgnr = p.orgnr;
                const created = p.created_at ? new Date(p.created_at).toLocaleDateString() : "—";
                const isBusy = busyKey === p.workspace_key;
                const isMenuOpen = menuOpenFor === p.workspace_key;
                const isConfirmOpen = confirmOpenFor === p.workspace_key;

                return (
                  <tr key={p.workspace_key || orgnr || p.id} className={isBusy ? "projects-row--busy" : ""}>
                    <td>
                      <Link className="projects-link" to={workspaceLandingUrl(p)}>
                        {p.name || "Unnamed company"}
                      </Link>
                      <div className="root-text-muted root-mono" style={{ marginTop: 6 }}>
                        {orgnr || "—"}
                      </div>
                    </td>

                    <td className="root-text-muted">{created}</td>

                    <td>
                      <StatusBadge status={p.status} />
                    </td>

                    <td className="projects-menuCell">
                      <div className="projects-menuWrap" ref={isMenuOpen ? menuRef : null}>
                        <button
                          type="button"
                          className="projects-menuBtn"
                          aria-label="Projektåtgärder"
                          aria-expanded={isMenuOpen ? "true" : "false"}
                          disabled={isBusy}
                          onClick={() => {
                            setConfirmOpenFor(null);
                            setMenuOpenFor((prev) => (prev === p.workspace_key ? null : p.workspace_key));
                          }}
                        >
                          ⋯
                        </button>

                        {isMenuOpen && (
                          <div className="projects-menu">
                            {/* Status transitions are system-managed
                                (Stripe webhook + cron). The only
                                broker-initiated transition is closing
                                an active or archived workspace. */}
                            {(p.status === "active" || p.status === "archived") && (
                              <>
                                <div className="projects-menuTitle">Avsluta affär</div>
                                <button
                                  type="button"
                                  className="projects-menuItem"
                                  onClick={async () => {
                                    if (!window.confirm(
                                      "Stänga affären? Workspace går i läsläge i minst 7 dagar " +
                                      "(eller till månadens slut), sen sparkas köpare och säljare ut. " +
                                      "Går inte att ångra."
                                    )) return;
                                    setBusyKey(p.workspace_key);
                                    setMenuOpenFor(null);
                                    try {
                                      const { closeWorkspaceBilling } = await import("../services/server");
                                      const resp = await closeWorkspaceBilling(p.workspace_key);
                                      updateLocalStatus(p.workspace_key, resp?.deal_status || "closing");
                                    } catch (e) {
                                      setError(e?.body?.detail || e?.message || "Kunde inte stänga affären.");
                                    } finally {
                                      setBusyKey(null);
                                    }
                                  }}
                                  disabled={isBusy}
                                  style={{ color: "#991B1B" }}
                                >
                                  Stäng affär
                                </button>
                                <div className="projects-menuDivider" />
                              </>
                            )}

                            {isConfirmOpen ? (
                              <div className="projects-confirm">
                                <div className="projects-confirmText">Ta bort projektet (admin) eller lämna (icke-admin)?</div>
                                <div className="projects-confirmActions">
                                  <button
                                    type="button"
                                    className="projects-confirmBtn projects-confirmBtn--danger"
                                    onClick={() => handleRemoveOrLeave(p)}
                                    disabled={isBusy}
                                  >
                                    Ja, fortsätt
                                  </button>
                                  <button
                                    type="button"
                                    className="projects-confirmBtn"
                                    onClick={() => setConfirmOpenFor(null)}
                                    disabled={isBusy}
                                  >
                                    Avbryt
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="projects-menuItem projects-menuItem--danger"
                                onClick={() => setConfirmOpenFor(p.workspace_key)}
                                disabled={isBusy}
                              >
                                Ta bort / Lämna
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Main>
  );
}

